<template>
  <div id="documentation" data-documentation-page class="gr-root">
    <div class="gr-container gr-arsip__head">
      <p class="gr-eyebrow" aria-hidden="true">
        <span class="gr-eyebrow__index">Arsip evidensi kegiatan</span>
        <span class="gr-eyebrow__rule" />
      </p>
      <h1 class="gr-arsip__title">Dokumentasi Kegiatan</h1>
      <p class="gr-arsip__intro">
        Catatan visual kegiatan Gotong Royong Indonesia, disusun dari waktu dan
        lokasi pelaksanaannya.
      </p>
    </div>

    <div class="gr-arsip__filters">
      <div class="gr-container gr-arsip__filters-inner">
        <div
          class="gr-arsip__filter-row"
          role="group"
          aria-label="Filter dokumentasi"
        >
          <v-select
            v-model="selectedYear"
            :items="yearOptions"
            label="Tahun"
            variant="outlined"
            density="comfortable"
            hide-details
            clearable
            :loading="optionsPending"
            :menu-props="{ class: 'gr-overlay' }"
            class="gr-arsip__filter"
          />
          <!-- Canonical asks the visitor to type the activity's slug. The
                   options here are built from the archive itself, so the
                   visitor picks a title and the slug is sent for them.

                   `documentations.activity` is nullable, so this list is empty
                   whenever nothing in the archive is linked to an activity —
                   which is not the same as "no activity has documentation".
                   Saying that would contradict the entries visible below. The
                   empty text names the missing link, not a missing activity. -->
          <v-select
            v-model="selectedActivity"
            :items="activityOptions"
            item-title="title"
            item-value="slug"
            label="Aktivitas"
            variant="outlined"
            density="comfortable"
            hide-details
            clearable
            :loading="optionsPending"
            :no-data-text="'Belum ada dokumentasi yang terhubung ke aktivitas'"
            :menu-props="{ class: 'gr-overlay' }"
            class="gr-arsip__filter gr-arsip__filter--wide"
          />
          <button
            v-if="hasAnyFilter"
            type="button"
            class="gr-btn gr-btn--outline"
            @click="clearFilters"
          >
            Hapus filter
          </button>
        </div>
      </div>
    </div>

    <div class="gr-container gr-arsip__body">
      <p v-if="error" data-error-state class="gr-arsip__alert" role="alert">
        <span>
          Dokumentasi belum dapat dimuat. Coba lagi tanpa menghapus hasil yang
          sudah tampil.
        </span>
        <button
          type="button"
          class="gr-btn gr-btn--outline"
          @click="() => refresh()"
        >
          Coba lagi
        </button>
      </p>

      <section aria-labelledby="gr-arsip-heading">
        <header class="gr-head gr-arsip__section-head">
          <h2 id="gr-arsip-heading" class="gr-h2">Arsip kegiatan</h2>
          <p class="gr-arsip__count" aria-live="polite">
            {{ countText }}
          </p>
        </header>

        <div
          v-if="pending && !items.length"
          class="gr-arsip__grid"
          aria-label="Memuat dokumentasi"
        >
          <ActivityCardSkeleton v-for="i in 6" :key="i" />
        </div>

        <div
          v-else-if="!items.length && !error"
          data-empty-state
          class="gr-arsip__empty"
        >
          <v-icon
            icon="mdi-image-search-outline"
            size="40"
            aria-hidden="true"
            class="gr-arsip__empty-icon"
          />
          <p class="gr-arsip__empty-title">Belum ada dokumentasi</p>
          <p class="gr-arsip__empty-text">
            {{
              hasAnyFilter
                ? "Coba ubah tahun atau aktivitas yang dipilih."
                : "Dokumentasi kegiatan akan tampil di sini setelah dipublikasikan."
            }}
          </p>
          <button
            v-if="hasAnyFilter"
            type="button"
            class="gr-btn gr-btn--outline mt-4"
            @click="clearFilters"
          >
            Hapus filter
          </button>
        </div>

        <div v-else class="gr-arsip__grid">
          <article
            v-for="item in items"
            :key="item.uuid"
            data-documentation-card
            class="gr-arsip__card"
          >
            <button
              type="button"
              class="gr-arsip__link"
              @click="openDocumentation(item)"
            >
              <div class="gr-arsip__media">
                <img
                  v-if="
                    item.photos[0]?.imageUrl && !failedImages.has(item.uuid)
                  "
                  :ref="(el) => setImageElement(item.uuid, el)"
                  :src="item.photos[0].imageUrl"
                  :alt="item.photos[0].altText"
                  data-documentation-image
                  class="gr-arsip__img"
                  loading="lazy"
                  @error="markImageError(item.uuid)"
                />
                <div
                  v-else
                  class="gr-arsip__img gr-arsip__fallback"
                  aria-label="Gambar tidak tersedia"
                >
                  <v-icon
                    icon="mdi-image-off-outline"
                    size="28"
                    aria-hidden="true"
                  />
                </div>
                <span
                  v-if="item.photos.length > 1"
                  class="gr-arsip__count-badge"
                >
                  <v-icon
                    icon="mdi-image-multiple-outline"
                    size="13"
                    aria-hidden="true"
                  />
                  {{ item.photos.length }}
                </span>
              </div>
              <div class="gr-arsip__copy">
                <h3 class="gr-arsip__card-title">
                  {{ item.title }}
                </h3>
                <p class="gr-arsip__meta">
                  <span>{{
                    formatDocumentationDate(item.documentationDate)
                  }}</span>
                  <span aria-hidden="true">&middot;</span>
                  <span>{{ item.documentationLocation }}</span>
                </p>
                <span class="gr-arsip__read">
                  Buka dokumentasi
                  <v-icon icon="mdi-arrow-right" size="16" aria-hidden="true" />
                </span>
              </div>
            </button>
          </article>
        </div>

        <div v-if="hasMore && items.length" class="gr-arsip__more">
          <button
            ref="loadMoreButton"
            type="button"
            class="gr-btn gr-btn--outline"
            :disabled="pending"
            @click="loadMore"
          >
            {{ pending ? "Memuat…" : "Muat lebih banyak dokumentasi" }}
          </button>
        </div>
      </section>

      <section
        class="gr-arsip__cta gr-section--panel"
        aria-labelledby="gr-arsip-cta"
      >
        <p class="gr-arsip__cta-eyebrow" aria-hidden="true">
          Langkah berikutnya
        </p>
        <h2 id="gr-arsip-cta" class="gr-arsip__cta-title">
          Ikuti kegiatan berikutnya
        </h2>
        <p class="gr-arsip__cta-text">
          Mari Jadi Bagian Dari Kegiatan Positif yang Berdampak Pada Masyarakat
        </p>
        <NuxtLink
          :to="{
            path: '/activities',
            query: { status: 'ON_REGISTRATION' },
          }"
          class="gr-btn gr-btn--solid gr-btn--on-panel gr-arsip__cta-btn"
        >
          Cari aktivitas
          <v-icon icon="mdi-arrow-right" size="18" aria-hidden="true" />
        </NuxtLink>
      </section>

      <DocumentationDialog
        :open="isDialogOpen"
        :uuid="activeUuid"
        :summary="activeSummary"
        @close="closeDocumentation"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import ActivityCardSkeleton from "@/features/Activity/views/components/ActivityCardSkeleton.vue";
import DocumentationDialog from "@/features/documentation/views/components/DocumentationDialog.vue";
import {
  formatDocumentationDate,
  getDocumentationRows,
  type DocumentationFilters,
  type DocumentationResponse,
  type PublicDocumentation,
} from "~/features/documentation/documentation";
import { usePublicDocumentations } from "~/features/documentation/usePublicDocumentations";

const PAGE_SIZE = 12;

const route = useRoute();
const router = useRouter();

// The page counter is state, never a query parameter. As a load-more list its
// URL describes the filters only: `?page=2` on a refresh fetched page two on its
// own and rendered 2 of 14 rows with no way back to the first page. This mirrors
// the activities list, which keeps its page count in the composable.
const page = ref(1);
const selectedYear = ref<string | null>(
  typeof route.query.year === "string" ? route.query.year : null,
);
const selectedActivity = ref<string | null>(
  typeof route.query.activitySlug === "string"
    ? route.query.activitySlug
    : null,
);

const filters = computed<DocumentationFilters>(() => ({
  page: page.value,
  size: PAGE_SIZE,
  year: selectedYear.value || undefined,
  activitySlug: selectedActivity.value || undefined,
}));

const { rows, meta, pending, error, refresh } =
  usePublicDocumentations(filters);

// Pages accumulate rather than replace. `usePublicDocumentations` is keyed on
// the query and returns one page at a time, so earlier pages are held here and
// the current page is read straight from `rows`.
//
// A computed, not a ref fed by a watcher: `rows` is itself a computed over
// async data, so a watcher with `immediate` snapshots it while it is still
// empty during server rendering, and the page ships its empty state with a
// "0 dari 14" counter before the client corrects it.
const previousPages = ref<PublicDocumentation[]>([]);
const items = computed(() => [...previousPages.value, ...rows.value]);

const hasMore = computed(() => items.value.length < meta.value.total);

const loadMoreButton = ref<HTMLButtonElement | null>(null);

// Same rule as the activities list: operated by keyboard the focus moves to the
// first new result and the browser scrolls to it; operated by pointer nothing
// moves, because the visitor can already see the new cards.
async function loadMore(): Promise<void> {
  const viaKeyboard = loadMoreButton.value?.matches(":focus-visible") ?? false;
  const firstNewIndex = items.value.length;
  previousPages.value = [...previousPages.value, ...rows.value];
  page.value += 1;
  if (!viaKeyboard) return;
  await nextTick();
  document
    .querySelector<HTMLElement>(
      `[data-documentation-card]:nth-of-type(${firstNewIndex + 1}) button`,
    )
    ?.focus();
}

const isDialogOpen = ref(false);
const activeUuid = ref<string | null>(null);
const activeSummary = ref<PublicDocumentation | null>(null);

// These dialogs are driven by a one-way `:open` prop plus a `@close` event
// rather than `v-model`, so Vuetify never sees the binding it uses to restore
// focus and drops it on `<body>` when the dialog closes. A keyboard user who
// pressed Escape would then have to tab from the top of the document to get
// back to the card they opened (WCAG 2.4.3). Hold the trigger ourselves.
let dialogTrigger: HTMLElement | null = null;

function openDocumentation(item: PublicDocumentation): void {
  dialogTrigger =
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
  activeUuid.value = item.uuid;
  activeSummary.value = item;
  isDialogOpen.value = true;
}

function closeDocumentation(): void {
  isDialogOpen.value = false;
  const trigger = dialogTrigger;
  dialogTrigger = null;
  // preventScroll: the archive can be scrolled well past the card, and the
  // default scroll-on-focus would yank the page the way "muat lebih banyak"
  // used to.
  if (trigger) void nextTick(() => trigger.focus({ preventScroll: true }));
}

// The API exposes no endpoint for either filter's options, so they are derived
// from the archive itself: one unfiltered read, then the distinct years and
// activities actually present. Canonical instead offers a hard-coded eight-year
// countdown from today and a free-text box for the activity's slug.
//
// ponytail: one page of 200 backs the pickers. If the archive ever outgrows
// that, this needs a dedicated facets endpoint rather than a bigger number.
const { $customFetch } = useNuxtApp();
const { data: optionsData, pending: optionsPending } = useAsyncData(
  "site:documentation-facets",
  () =>
    $customFetch<DocumentationResponse>(
      "/public-documentations?page=1&size=200",
    ),
);

const allRows = computed(() => getDocumentationRows(optionsData.value ?? {}));

const yearOptions = computed(() =>
  [
    ...new Set(
      allRows.value
        .map((row) => row.documentationDate?.slice(0, 4))
        .filter((year): year is string => Boolean(year)),
    ),
  ].sort((a, b) => Number(b) - Number(a)),
);

const activityOptions = computed(() => {
  const seen = new Map<string, { slug: string; title: string }>();
  for (const row of allRows.value) {
    const activity = row.activity;
    if (activity?.slug && !seen.has(activity.slug)) {
      seen.set(activity.slug, { slug: activity.slug, title: activity.title });
    }
  }
  return [...seen.values()].sort((a, b) =>
    a.title.localeCompare(b.title, "id"),
  );
});

const hasAnyFilter = computed(() =>
  Boolean(selectedYear.value || selectedActivity.value),
);

const countText = computed(() => {
  if (!meta.value.total) return "Belum ada dokumentasi yang ditampilkan.";
  return `Menampilkan ${items.value.length} dari ${meta.value.total} dokumentasi.`;
});

// Filters apply as they are chosen. Canonical gathers them into drafts behind a
// "Terapkan filter" button; with two selects there is nothing to batch, and the
// result count reports the outcome either way.
function syncQuery(): void {
  void router.push({
    query: {
      ...(selectedYear.value ? { year: selectedYear.value } : {}),
      ...(selectedActivity.value
        ? { activitySlug: selectedActivity.value }
        : {}),
    },
  });
}

watch([selectedYear, selectedActivity], () => {
  page.value = 1;
  previousPages.value = [];
  syncQuery();
});

function clearFilters(): void {
  selectedYear.value = null;
  selectedActivity.value = null;
}

const imageElements = new Map<string, HTMLImageElement>();
const failedImages = ref(new Set<string>());
const setImageElement = (
  uuid: string,
  element: Element | ComponentPublicInstance | null,
): void => {
  if (element instanceof HTMLImageElement) imageElements.set(uuid, element);
  else imageElements.delete(uuid);
};
const markImageError = (uuid: string): void => {
  failedImages.value = new Set(failedImages.value).add(uuid);
};
onMounted(() => {
  for (const [uuid, image] of imageElements) {
    if (image.complete && image.naturalWidth === 0) markImageError(uuid);
  }
});

useHead({
  title: "Dokumentasi Kegiatan",
});
</script>

<style scoped src="@/features/documentation/views/documentation.css"></style>
